/**
 * @fileoverview Filter context definition and types.
 * @module shared/context/FilterContext
 */

import { createContext } from 'react'
// Source Variant type from the canonical variantService to ensure fields match (nullable optionals)
import { type Variant } from '@/lib/variantService'
import { type Tag } from '@/features/tags'

/**
 * Available time aggregation options for data grouping.
 */
export type AggregationType = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly'

/**
 * Supported country data structure.
 */
export interface SupportedCountryDTO {
  /** Country code (e.g., 'PL', 'SK') */
  code: string
  /** Display name (e.g., 'Poland', 'Slovakia') */
  name: string
}

/**
 * Context type definition for filter state and actions.
 */
export interface FilterContextType {
  // Date range filter
  /** Start date for data filtering */
  startDate: Date | null
  /** End date for data filtering */
  endDate: Date | null
  /** Function to update date range */
  setDateRange: (start: Date | null, end: Date | null) => void

  // Aggregation filter
  /** Current time aggregation setting */
  aggregation: AggregationType
  /** Function to update aggregation setting */
  setAggregation: (aggregation: AggregationType) => void

  // Countries filter
  /** Selected country codes */
  countries: string[]
  /** Function to update selected countries */
  setCountries: (countries: string[]) => void

  // Available countries from API (placeholder for future implementation)
  /** All available countries for selection */
  availableCountries: SupportedCountryDTO[]
  /** Countries loading state */
  isLoadingCountries: boolean

  // Variants filter (legacy property names for backward compatibility)
  /** Selected variant names */
  skus: string[]
  /** Function to update selected variants */
  setSkus: (skus: string[]) => void

  // Available variants from API
  /** All available variants for selection */
  availableSkus: Variant[]
  /** Variants loading state */
  isLoadingSkus: boolean

  // Tags filter
  /** Selected tag UUIDs */
  tags: string[]
  /** Function to update selected tags */
  setTags: (tags: string[]) => void

  // Available tags from API
  /** All available tags for selection */
  availableTags: Tag[]
  /** Tags loading state */
  isLoadingTags: boolean

  // Profit basis (CM2 vs Full Profit)
  /** Current profit metric basis used across TIME-series views */
  profitBasis: 'OC' | 'CM2'
  /** Update the current profit metric basis */
  setProfitBasis: (basis: 'OC' | 'CM2') => void
}

/**
 * Filter context for managing global filter state.
 */
export const FilterContext = createContext<FilterContextType | undefined>(undefined)
